// w03 / w12 Watercolor — paper-invitation preview, botanical mood.
// Outer wash foil from template's accent + bg, inner card.
// Eyebrow "TO'Y MAROSIMI" → watercolor-wreath photo medallion → names
// in template font → date + venue caption.

import { Photo, type LayoutCtx } from "./_shared";

export function WatercolorLayout({
  names, date, venue, accent, ink, bg, font, photo, photos,
}: LayoutCtx) {
  const captionDate = formatDate(date);
  const hero = photos[0] || photo;
  const FOIL = `linear-gradient(135deg, ${accent}cc 0%, ${accent}55 22%, ${bg} 45%, ${accent}55 65%, ${accent}cc 100%)`;
  return (
    <div style={{ position: "absolute", inset: 0, background: FOIL, padding: "5%", display: "flex" }}>
      <div
        style={{
          flex: 1,
          background: bg,
          color: ink,
          position: "relative",
          padding: "8%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          boxShadow: `inset 0 0 0 1px ${accent}55`,
          overflow: "hidden",
        }}
      >
        {/* Soft watercolor blooms */}
        <svg
          aria-hidden viewBox="0 0 200 280" preserveAspectRatio="none"
          style={{ position: "absolute", inset: 0, width: "100%", height: "100%", opacity: 0.45 }}
        >
          <defs>
            <filter id="w12-paint" x="-20%" y="-20%" width="140%" height="140%">
              <feTurbulence type="fractalNoise" baseFrequency="0.9" numOctaves="2" seed="3" />
              <feDisplacementMap in="SourceGraphic" scale="3" />
            </filter>
          </defs>
          <g filter="url(#w12-paint)">
            <ellipse cx="32" cy="38" rx="44" ry="26" fill={accent} opacity="0.35" />
            <ellipse cx="168" cy="248" rx="48" ry="28" fill={accent} opacity="0.35" />
          </g>
        </svg>

        <div style={{ fontFamily: "var(--font-ui)", fontSize: 7, fontWeight: 700, letterSpacing: "0.4em", color: accent, marginBottom: "5%", position: "relative" }}>
          TO&apos;Y MAROSIMI
        </div>

        {/* Watercolor wreath photo medallion */}
        <div style={{ width: "62%", aspectRatio: "1/1", position: "relative", marginBottom: "5%" }}>
          <svg viewBox="0 0 100 100" style={{ position: "absolute", inset: -2, width: "calc(100% + 4px)", height: "calc(100% + 4px)" }} aria-hidden>
            {Array.from({ length: 24 }).map((_, i) => {
              const ang = (i / 24) * Math.PI * 2;
              const cx = 50 + Math.cos(ang) * 48;
              const cy = 50 + Math.sin(ang) * 48;
              const len = 3.5 + ((i * 7) % 4) * 0.4;
              const rot = (ang * 180) / Math.PI + 90;
              return (
                <g key={i} transform={`translate(${cx} ${cy}) rotate(${rot})`}>
                  <ellipse rx={len} ry="1.2" fill={accent} opacity="0.7" />
                </g>
              );
            })}
            <circle cx="50" cy="50" r="40" fill="none" stroke={accent} strokeWidth="0.4" opacity="0.6" />
          </svg>
          <div
            style={{
              position: "absolute",
              inset: "10%",
              borderRadius: "50%",
              overflow: "hidden",
              border: `1px solid ${accent}88`,
              background: bg,
            }}
          >
            <Photo
              src={hero}
              style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
              fallback={
                <div
                  style={{
                    position: "absolute",
                    inset: 0,
                    background: `radial-gradient(circle at 50% 40%, ${accent}33, ${bg} 75%)`,
                    display: "grid",
                    placeItems: "center",
                    fontFamily: font,
                    fontStyle: "italic",
                    fontSize: 16,
                    color: accent,
                  }}
                >
                  ✿
                </div>
              }
            />
            <div style={{ position: "absolute", inset: 0, background: `radial-gradient(circle at 50% 30%, transparent 50%, ${accent}33 100%)`, mixBlendMode: "multiply" }} />
          </div>
        </div>

        <div style={{ fontFamily: font, fontStyle: "italic", fontSize: 28, lineHeight: 1.05, color: ink, position: "relative" }}>
          {names}
        </div>

        <div style={{ flex: 1 }} />

        <div style={{ fontFamily: "var(--font-ui)", fontSize: 6.5, fontWeight: 700, letterSpacing: "0.32em", color: accent, marginTop: "4%", position: "relative" }}>
          MAROSIM · {captionDate}
        </div>
        <div style={{ fontFamily: "var(--font-ui)", fontSize: 6, fontWeight: 600, letterSpacing: "0.28em", color: `${ink}99`, marginTop: 6, position: "relative" }}>
          {venue.toUpperCase()}
        </div>
      </div>
    </div>
  );
}

function formatDate(d: string): string {
  const m = d.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
  if (!m) return d.toUpperCase();
  const months = [
    "YANVAR", "FEVRAL", "MART", "APREL", "MAY", "IYUN",
    "IYUL", "AVGUST", "SENTABR", "OKTABR", "NOYABR", "DEKABR",
  ];
  return `${parseInt(m[1], 10)} ${months[parseInt(m[2], 10) - 1] ?? ""} ${m[3]}`;
}
